import { ChangeEvent, DragEvent, FormEvent } from 'react'

import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { Button } from '../button/button.js'
import './badge-claim-form.scss'

export interface BadgeClaimFormProps {
    lang: Language
    comment: string
    onCommentChange: (v: string) => void
    files: File[]
    onFilesChange: (files: File[]) => void
    isSubmitting: boolean
    onSubmit: () => void
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml,application/pdf'
const OVER_CLASS = 'badge-claim-form__drop--over'

export const BadgeClaimForm = (props: BadgeClaimFormProps) => {
    const { lang } = props
    const b = t.playtime.badges
    const canSubmit = props.comment.trim().length > 0 && !props.isSubmitting

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()
        if (canSubmit) props.onSubmit()
    }
    const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) props.onFilesChange([...props.files, ...Array.from(e.target.files)])
        e.target.value = ''
    }
    const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
        e.preventDefault()
        e.currentTarget.classList.remove(OVER_CLASS)
        if (e.dataTransfer.files.length) {
            props.onFilesChange([...props.files, ...Array.from(e.dataTransfer.files)])
        }
    }
    const handleDragOver = (e: DragEvent<HTMLLabelElement>) => {
        e.preventDefault()
        e.currentTarget.classList.add(OVER_CLASS)
    }
    const handleDragLeave = (e: DragEvent<HTMLLabelElement>) => {
        e.currentTarget.classList.remove(OVER_CLASS)
    }

    return (
        <form className="badge-claim-form" onSubmit={handleSubmit}>
            <p className="badge-claim-form__title">{b.claimTitle(lang)}</p>
            <label className="badge-claim-form__label" htmlFor="badge-claim-comment">
                {b.comment(lang)}
            </label>
            <textarea
                id="badge-claim-comment"
                className="badge-claim-form__textarea"
                rows={3}
                value={props.comment}
                placeholder={b.claimCommentPlaceholder(lang)}
                onChange={(e) => props.onCommentChange(e.target.value)}
                required
            />
            <span className="badge-claim-form__label" id="badge-claim-files-label">
                {b.evidence(lang)}
            </span>
            <label
                className="badge-claim-form__drop"
                aria-labelledby="badge-claim-files-label"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <span>
                    <b>{b.dropzone(lang)}</b> {b.dropzoneOr(lang)}
                </span>
                <span>{b.dropzoneHint(lang)}</span>
                <input
                    type="file"
                    className="badge-claim-form__input"
                    accept={ACCEPT}
                    multiple
                    onChange={handleInput}
                />
            </label>
            {props.files.map((file, i) => (
                <span key={`${file.name}-${i}`} className="badge-claim-form__file">
                    <span className="badge-claim-form__file-name">{file.name}</span>
                    <button
                        type="button"
                        className="badge-claim-form__file-remove"
                        aria-label={b.removeFile(lang)}
                        onClick={() => props.onFilesChange(props.files.filter((_, j) => j !== i))}
                    >
                        ×
                    </button>
                </span>
            ))}
            <Button
                className="badge-claim-form__submit"
                text={props.isSubmitting ? b.submitting(lang) : b.submitClaim(lang)}
                variant="primary"
                isDisabled={!canSubmit}
                buttonProps={{ type: 'submit' }}
            />
        </form>
    )
}
